import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';

import { ENV_VARS } from '@/env';
import { isNumber } from '@/utils/number';

import { ReactComponent as DeleteIcon } from '../../../../../../assets/icons/delete-icon.svg';
import {
  removeCurrentQuoteCollaborators,
  resetCurrentQuoteCollaborators,
  setCurrentQuoteCollaborators,
} from '../../../../../../redux/common/CommonActions';
import { selectCurrentQuoteCollaborators } from '../../../../../../redux/common/CommonReducer';
import Button from '../../../../../components/Buttons/Button';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormCheckBox from '../../../../../components/Inputs/FormCheckBox';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import Drawer from '../../../../../components/Modals/Drawer';
import { useGetQuoteCollaboratorsList } from '../../../hooks';
import CollaboratorsModal from '../../common/CollaboratorsModal';
import HalQuoteCollaboratorsModal from '../../common/HalQuoteCollaboratorsModal';

const QuoteCollaboratorLogs = () => {
  const [visible, setVisible] = useState(false);
  const [checkedCollaboratorsIds, setCheckedCollaboratorsIds] = useState([]);

  const dispatch = useDispatch();
  const copiedCollaboratorsSeededRef = useRef(false);
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);
  const currentCollaborators = useSelector(selectCurrentQuoteCollaborators);
  const CollaboratorsModalComponent = isHBUser ? HalQuoteCollaboratorsModal : CollaboratorsModal;

  const { data: { collaborators } = [] } = useGetQuoteCollaboratorsList(copyQuoteKey);

  useEffect(() => {
    dispatch(resetCurrentQuoteCollaborators());
  }, [dispatch]);

  useEffect(() => {
    copiedCollaboratorsSeededRef.current = false;
  }, []);

  // Copy collaborators if it is a copy of a quote (once per copy session)
  useEffect(() => {
    if (copyQuoteKey && collaborators?.length && !copiedCollaboratorsSeededRef.current) {
      copiedCollaboratorsSeededRef.current = true;
      dispatch(
        setCurrentQuoteCollaborators(
          collaborators.map((item) => ({
            emails: item?.emails,
            group: item?.group,
            notificationsEnabled: item?.notificationsEnabled,
            role: item?.role,
            userId: item?.userId,
            userName: item?.userName,
          })),
        ),
      );
    }
  }, [collaborators, copyQuoteKey, dispatch]);

  const handleCloseDrawer = () => {
    setVisible(false);
  };

  const handleCheckCollaborators = (e, checkedId) => {
    if (e.target.checked) {
      setCheckedCollaboratorsIds([...checkedCollaboratorsIds, checkedId]);
    } else {
      setCheckedCollaboratorsIds(checkedCollaboratorsIds.filter((id) => id !== checkedId));
    }
  };

  const handleDeleteCollaborators = (collaboratorId) => {
    const selected = isNumber(collaboratorId) ? [collaboratorId] : checkedCollaboratorsIds;
    dispatch(removeCurrentQuoteCollaborators(selected));
    setCheckedCollaboratorsIds([]);
  };

  return (
    <div className="h-100 custom-scroll-bar">
      <Row justify="end" align="middle">
        <Col className="px-15 py-10">
          <Row gutter={15}>
            <Col>
              <Button dataTestId="quote-collborator-export-button" className={isHBUser ? 'hb-button' : ''}>
                Export to Excel
              </Button>
            </Col>

            <Col>
              <Button
                dataTestId="quote-collborator-add-user-button"
                className={isHBUser ? 'hb-button' : ''}
                onClick={() => setVisible(true)}
              >
                Add Users
              </Button>

              <Drawer
                width="70%"
                open={visible}
                headerContents={<PrimaryLabel className="text-white">Collaborator Search</PrimaryLabel>}
                onClose={() => setVisible(false)}
              >
                <CollaboratorsModalComponent collaborators={currentCollaborators} handleClose={handleCloseDrawer} />
              </Drawer>
            </Col>

            <Col>
              <Button
                disabled={checkedCollaboratorsIds?.length === 0}
                dataTestId="quote-collborator-remote-user-button"
                className={isHBUser ? 'hb-button' : ''}
                onClick={handleDeleteCollaborators}
              >
                Remove User
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <div style={{ height: '200px', overflowY: 'scroll' }}>
        {currentCollaborators.length >= 1 &&
          currentCollaborators?.map((collaborator) => (
            <div
              className="paper-container border-muted mb-15 ant-collapse-header guide-card-container py-0"
              key={collaborator.userId}
            >
              <div className="py-10">
                <Row gutter={10} className="flex-nowrap px-15" justify="start">
                  <Col sm={4} className="d-flex align-items-center">
                    <FormCheckBox
                      checkBoxClass="normal-checkbox"
                      className="mb-0 mr-10"
                      checked={checkedCollaboratorsIds.includes(collaborator.userId)}
                      onChange={(e) => handleCheckCollaborators(e, collaborator.userId)}
                    />
                    <div>
                      <p className="m-0 fw-400 f-12 color-label">User Name</p>
                      <p className="mt-1 fw-500 f-13 color-dark">{collaborator.userName}</p>
                    </div>
                  </Col>
                  <Col sm={8}>
                    <p className="m-0 fw-400 f-12 color-label">Email </p>
                    <p className="mt-1 fw-600 f-13 color-dark">{collaborator.emails}</p>
                  </Col>
                  <Col sm={5}>
                    <p className="m-0 fw-400 f-12 color-label">Role</p>
                    <p className="mt-1 fw-500 f-13 color-dark">{collaborator.role}</p>
                  </Col>
                  <Col sm={4}>
                    <p className="m-0 fw-400 f-12 color-label">Notification e-mails</p>
                    <p className="mt-1 fw-500 f-13 color-dark">{collaborator.notificationsEnabled ? 'On' : 'Off'}</p>
                  </Col>
                  <Col sm={2} className="d-flex justify-content-center hidden-buttons">
                    <IconButton className="ml-16" onClick={() => handleDeleteCollaborators(collaborator.userId)}>
                      <SvgConverter Icon={DeleteIcon} color="iconDanger" width={16} height={16} />
                    </IconButton>
                  </Col>
                </Row>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default memo(QuoteCollaboratorLogs);
